// Typed API client for the /v1 contract. Attaches the Cognito Bearer token, validates
// every response with the shared-contract Zod schema at the boundary (BR4.1 parity),
// maps an RFC 7807 Problem body to a typed error, and signals 401 for re-auth.
import { z } from "zod";
import {
  accountSchema,
  accountsResponseSchema,
  assumptionsSchema,
  dashboardResponseSchema,
  monteCarloResponseSchema,
  problemSchema,
  projectionResponseSchema,
  retirementResponseSchema,
  type Account,
  type AccountInput,
  type AccountsResponse,
  type Assumptions,
  type AssumptionsInput,
  type DashboardResponse,
  type MonteCarloRequest,
  type MonteCarloResponse,
  type ProjectionResponse,
  type ProblemCode,
  type RetirementRequest,
  type RetirementResponse,
} from "@retirement-planner/contract";
import { clearToken, getToken } from "./auth";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "/v1").replace(/\/$/, "");

/** A typed API error carrying the closed ProblemCode (never raw server text).
 * `ABORTED` is a superseded/cancelled request (not a real failure); `NETWORK` is a
 * genuine connectivity error; `PARSE` is a contract-drift rejection. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ProblemCode | "NETWORK" | "PARSE" | "ABORTED",
  ) {
    super(code);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(
  path: string,
  schema: z.ZodType<T> | null,
  opts: RequestOptions = {},
): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: opts.method ?? "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    // An aborted (superseded) request is not a real failure — distinguish it so
    // callers can ignore stale commits while still surfacing genuine network errors.
    if (opts.signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) {
      throw new ApiError(0, "ABORTED");
    }
    throw new ApiError(0, "NETWORK");
  }

  if (res.status === 401) {
    clearToken();
    throw new ApiError(401, "UNAUTHORIZED");
  }
  if (res.status === 204) return undefined as T;

  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const problem = problemSchema.safeParse(json);
    throw new ApiError(res.status, problem.success ? problem.data.code : "SERVER_ERROR");
  }
  if (schema === null) return undefined as T;
  const parsed = schema.safeParse(json);
  if (!parsed.success) throw new ApiError(res.status, "PARSE"); // contract drift — never mis-render
  return parsed.data;
}

// -- endpoints --------------------------------------------------------------
export const api = {
  getDashboard: (signal?: AbortSignal): Promise<DashboardResponse> =>
    request("/dashboard", dashboardResponseSchema, { signal }),
  getAssumptions: (): Promise<Assumptions> => request("/assumptions", assumptionsSchema),
  putAssumptions: (body: AssumptionsInput): Promise<Assumptions> =>
    request("/assumptions", assumptionsSchema, { method: "PUT", body }),
  listAccounts: (): Promise<AccountsResponse> => request("/accounts", accountsResponseSchema),
  createAccount: (body: AccountInput): Promise<Account> =>
    request("/accounts", accountSchema, { method: "POST", body }),
  updateAccount: (id: string, body: AccountInput): Promise<Account> =>
    request(`/accounts/${id}`, accountSchema, { method: "PUT", body }),
  deleteAccount: (id: string): Promise<void> =>
    request(`/accounts/${id}`, null, { method: "DELETE" }),
  computeProjection: (body?: AssumptionsInput): Promise<ProjectionResponse> =>
    request("/projection", projectionResponseSchema, { method: "POST", body }),
  solveRetirement: (body?: RetirementRequest, signal?: AbortSignal): Promise<RetirementResponse> =>
    request("/retirement", retirementResponseSchema, { method: "POST", body, signal }),
  runMonteCarlo: (body?: MonteCarloRequest): Promise<MonteCarloResponse> =>
    request("/montecarlo", monteCarloResponseSchema, { method: "POST", body }),
};
